import { DOCUMENT, Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RoutesRecognized } from '@angular/router';

import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';

import { CookieName } from '../browser/cookie/cookie.models';
import { CookieService } from '../browser/cookie/cookie.service';
import { TimerService } from '../browser/timer.service';
import { WINDOW } from '../browser/window/window.token';
import { Page } from '../client-config/page.client-config';
import { LastKnownProductService } from '../last-known-product/last-known-product.service';
import { HeaderService } from '../lazy/service-providers/header.service';
import { LoadingIndicatorService } from '../loading-indicator/loading-indicator.service';
import { LoginStoreService } from '../login/login-store.service';
import { PostLoginService } from '../login/post-login.service';
import { MessageQueueService } from '../messages/message-queue.service';
import { NativeAppConfig } from '../native-app/native-app.client-config';
import { NativeEventType } from '../native-app/native-app.models';
import { NativeAppService } from '../native-app/native-app.service';
import { RouteDataOptions } from '../routing/route-data';
import { UserService } from '../user/user.service';
import { ImmutableParsedUrl } from './immutable-parsed-url';
import { GoToLastKnownProductOptions, GoToOptions, LocationChangeEvent, NativeAppGoToOptions, NavigationInfo } from './navigation.models';
import { ParsedUrl } from './parsed-url';
import { UrlService } from './url.service';

/**
 * @whatItDoes Provides utility functions for navigation.
 *
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class NavigationService {
    private previousLocation: string;
    private readonly routeDataOptions = new BehaviorSubject<RouteDataOptions | null>(null);
    private cachedLocationHref: string;
    private cachedParsedUrl: ImmutableParsedUrl;
    private readonly locationChanges = new Subject<LocationChangeEvent>();
    private readonly attemptedNavigations = new Subject<ParsedUrl>();
    private lastUrl: string;
    private readonly forceFullPageReload: RegExp | null;

    readonly #window = inject(WINDOW);
    private readonly _doc = inject(DOCUMENT);
    private readonly urlService = inject(UrlService);
    private readonly page = inject(Page);
    private readonly user = inject(UserService);
    private readonly nativeAppService = inject(NativeAppService);
    private readonly nativeAppConfig = inject(NativeAppConfig);
    private readonly router = inject(Router);
    private readonly messageQueueService = inject(MessageQueueService);
    private readonly loadingIndicatorService = inject(LoadingIndicatorService);
    private readonly lastKnownProductService = inject(LastKnownProductService);
    private readonly cookieService = inject(CookieService);
    private readonly timerService = inject(TimerService);
    private readonly headerService = inject(HeaderService);
    private readonly loginStoreService = inject(LoginStoreService);
    private readonly postLoginService = inject(PostLoginService);

    constructor() {
        this.locationChange.pipe(filter((l: LocationChangeEvent) => l.previousUrl !== l.nextUrl)).subscribe((event: LocationChangeEvent) => {
            this.previousLocation = event.previousUrl;
            this.nativeAppService.sendToNative({
                eventName: NativeEventType.LOCATION_CHANGE,
                parameters: event,
            });
        });
        this.forceFullPageReload = this.page.forceFullPageReloadPattern ? new RegExp(this.page.forceFullPageReloadPattern) : null;
    }

    /**
     * Provides current url parsed to parts.
     */
    get location(): ImmutableParsedUrl {
        const href = this.#window.location.href;

        if (this.cachedLocationHref !== href) {
            this.cachedParsedUrl = new ImmutableParsedUrl(this.urlService.parse(href));
            this.cachedLocationHref = href;
        }

        return this.cachedParsedUrl;
    }

    /**
     * Observable that provides the previous URL.
     */
    get previousUrl(): string {
        return this.previousLocation;
    }

    /**
     * Observable that provides the route data options. See: {@link RouteDataOptions}
     */
    get routeData(): Observable<RouteDataOptions | null> {
        return this.routeDataOptions;
    }

    /**
     * Notifies subscribers when location changes.
     */
    get locationChange(): Observable<LocationChangeEvent> {
        return this.locationChanges;
    }

    /**
     * Notifies subscribers on navigation attempts.
     */
    get attemptedNavigation(): Observable<ParsedUrl> {
        return this.attemptedNavigations;
    }

    /**
     * Navigates to a url. Based on whether the url is on the same host and same language
     * does a client side navigation (using `Router`) or full page reload (using `window.location.href`).
     */
    async goTo(url: string | ParsedUrl, options?: GoToOptions): Promise<any> {
        const opts = {
            ...options,
            showSpinnerForExternalNavigation: true,
        };
        const finalUrl = typeof url === 'string' ? this.urlService.parse(url) : url;

        if (this.lastUrl) {
            this.handleQueryParams(finalUrl, opts);
        }

        this.addGaTracking(finalUrl);
        this.handleReferrer(finalUrl, opts);
        this.handleMessageQueue(opts);
        this.handleCultureChange(finalUrl, opts);

        this.attemptedNavigations.next(finalUrl);

        if (finalUrl.protocol === 'htcmd') {
            this.#window.location.href = decodeURIComponent(`${finalUrl.protocol}:${finalUrl.url().substring(1)}`);
            return new Promise(() => {});
        }

        if (this.shouldForceReload(finalUrl, opts)) {
            return this.performFullPageNavigation(finalUrl, opts);
        }

        return this.performClientSideNavigation(finalUrl, opts);
    }

    /**
     * Navigate to a path in the overlay outlet.
     */
    goToOverlayOutlet(path: string, info?: NavigationInfo, searchQuery?: string): Promise<boolean> {
        const defaultInfo: NavigationInfo = { skipPrimaryOutletGuardsAndResolvers: true, skipTransition: false, isBackNavigation: false };

        const url = this.router.serializeUrl(this.router.createUrlTree([{ outlets: { overlay: path } }]));

        const finalUrl = searchQuery ? `${url}${searchQuery}` : url;

        return this.router.navigateByUrl(finalUrl, { info: { ...defaultInfo, ...info } });
    }

    /**
     * Navigates back to native app from wrapped web view. Uses special `bwin://` scheme.
     * If there are any messages in {@link MessageQueueService}, it
     * redirects to url provided by that constant.
     */
    goToNativeApp(options?: NativeAppGoToOptions) {
        if (this.messageQueueService.count() > 0) {
            this.goTo('/{culture}/nativeapp/backtoapp', options);
        } else {
            const nativeUrl = this.buildNativeUrl();
            const sendPostLogin = this.shouldSendPostLogin();

            this.navigateToNativeApp(nativeUrl, sendPostLogin, options);
        }
    }

    sendCloseCommand() {
        this.#window.location.href = this.nativeAppService.playtechNativeScheme + 'close';
    }

    /** Navigates to last known product */
    goToLastKnownProduct(options?: GoToLastKnownProductOptions) {
        this.handleGoToLastKnownProduct(options, false);
    }

    async goToLastKnownProductAsync(options?: GoToLastKnownProductOptions): Promise<void> {
        await this.handleGoToLastKnownProduct(options, true);
    }

    /** Navigates to return url i.e. rurl parameter */
    goToReturnUrl() {
        const url = this.cookieService.get(CookieName.Rurl);

        if (url) {
            this.goTo(url, { isBackNavigation: true });
        } else {
            this.goToLastKnownProduct();
        }
    }

    /** stores return url parameter to cookie. */
    storeReturnUrl() {
        // use either "rurl" or "ReturnUrl" (in case of Vanilla based unauth'd redirection)
        const rurlKeys = ['rurl', 'ReturnUrl'];

        const rurl = rurlKeys
            .map((key) => {
                const encoded = this.location.search.get(key);

                return encoded ? decodeURIComponent(encoded) : null;
            })
            .find((v) => v != null);

        if (rurl && !rurl.match(/(javascript|src|onerror|<|>)/g)) {
            this.cookieService.put(CookieName.Rurl, rurl);
        }
    }

    /**
     * @internal
     */
    init() {
        this.lastUrl = this.location.absUrl();

        this.router.events.pipe(filter((e): e is RoutesRecognized => e instanceof RoutesRecognized)).subscribe((e: RoutesRecognized) => {
            const data = this.getRouteData(e.state.root);

            this.routeDataOptions.next(data);
        });

        this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((e: NavigationEnd) => {
            const nextUrl = this.location.absUrl();

            this.locationChanges.next({
                id: e.id,
                previousUrl: this.lastUrl,
                nextUrl,
            });

            this.lastUrl = nextUrl;
        });
    }

    /** @internal */
    getRouteData(snapshot: ActivatedRouteSnapshot, base: any = {}): RouteDataOptions {
        if (!snapshot.children.length) {
            return snapshot.data;
        }

        return snapshot.children.reduce((a, c: ActivatedRouteSnapshot) => Object.assign(a, this.getRouteData(c, a)), base);
    }

    private handleQueryParams(finalUrl: ParsedUrl, opts: GoToOptions) {
        const lastUrlParsed = this.urlService.parse(this.lastUrl);
        const lastUrlQueryParams = lastUrlParsed.search;

        switch (opts.queryParamsHandling) {
            case 'merge':
                lastUrlQueryParams.keys().forEach((param) => {
                    if (!finalUrl.search.has(param)) {
                        finalUrl.search.append(param, lastUrlQueryParams.get(param)!);
                    }
                });
                break;
            case 'preserve':
                finalUrl.search = lastUrlQueryParams;
                break;
        }
    }

    private handleReferrer(finalUrl: ParsedUrl, opts: GoToOptions) {
        if (opts.appendReferrer) {
            if (typeof opts.appendReferrer === 'string') {
                finalUrl.appendReferrer({ url: opts.appendReferrer });
            } else {
                finalUrl.appendReferrer({ absolute: true });
            }
        }
    }

    private handleMessageQueue(opts: GoToOptions) {
        if (opts.storeMessageQueue) {
            this.messageQueueService.storeMessages();
        }
    }

    private handleCultureChange(finalUrl: ParsedUrl, opts: GoToOptions) {
        if (opts.culture && finalUrl.culture != null && opts.culture !== finalUrl.culture) {
            finalUrl.changeCulture(opts.culture);
        }
    }

    private shouldForceReload(finalUrl: ParsedUrl, opts: GoToOptions): boolean {
        const culture = finalUrl.culture ?? this.page.lang;

        return (
            opts.forceReload ||
            !finalUrl.isSameHost ||
            !culture ||
            culture !== this.page.lang ||
            this.forceFullPageReload?.test(finalUrl.path()) ||
            false
        );
    }

    private performFullPageNavigation(finalUrl: ParsedUrl, opts: GoToOptions): Promise<void> {
        const navigate = () => {
            const targetWindow = opts.forceReloadTarget ? this.#window[opts.forceReloadTarget] : this.#window;

            if (!targetWindow) return;

            if (opts.replace) {
                targetWindow.location.replace(finalUrl.absUrl());
            } else {
                targetWindow.location.href = finalUrl.absUrl();
            }
        };

        if (opts.showSpinnerForExternalNavigation && !finalUrl.isSameHost) {
            const loadingIndicator = this.loadingIndicatorService.start({
                blockScrolling: true,
                delay: this.page.loadingIndicator.externalNavigationDelay,
            });

            this.timerService.setTimeout(() => loadingIndicator.done(), 5000);
        }

        navigate();

        return new Promise(() => {});
    }

    private async performClientSideNavigation(finalUrl: ParsedUrl, opts: GoToOptions): Promise<void> {
        const navigationInfo: NavigationInfo = {
            isBackNavigation: !!opts.isBackNavigation,
            skipPrimaryOutletGuardsAndResolvers: !!opts.skipPrimaryOutletGuardsAndResolvers,
            skipTransition: !!opts.skipTransition,
        };

        await this.router.navigateByUrl(finalUrl.url(), {
            replaceUrl: !!opts.replace,
            skipLocationChange: !!opts.skipLocationChange,
            info: navigationInfo,
        });

        if (finalUrl.hash) {
            const element = this._doc.getElementById(finalUrl.hash);
            element?.scrollIntoView();
            await firstValueFrom(this.headerService.whenReady);
            this.#window.scrollTo(0, this.#window.scrollY - this.headerService.getHeaderHeight());
        }

        if (opts.storeMessageQueue) {
            this.messageQueueService.restoreMessages();
        }
    }

    private buildNativeUrl(): string {
        let nativeUrl = this.nativeAppService.nativeScheme;

        if (!this.nativeAppService.htcmdSchemeEnabled) {
            if (this.user.isAuthenticated) {
                nativeUrl += `${this.user.username}/${this.user.ssoToken}`;
            } else {
                nativeUrl += 'noSso';
            }

            const superCookie = this.cookieService.get(CookieName.SuperCookie);

            if (superCookie) {
                nativeUrl += `/${superCookie}`;
            } else {
                nativeUrl += this.buildPartnerSessionUrl();
            }
        } else if (this.user.id) {
            nativeUrl = this.nativeAppService.playtechNativeScheme;
            const postLoginValues = this.cookieService.getObject(CookieName.MobileLoginPostLoginValues);
            nativeUrl += `login?username=${this.user.id}&password=${postLoginValues?.tempPartnerToken ?? ''}&type=4`;
        }

        return nativeUrl;
    }

    private buildPartnerSessionUrl(): string {
        if (this.nativeAppConfig.partnerSessionIdSupported) {
            const postLoginValues = this.cookieService.get(CookieName.MobileLoginPostLoginValues);
            const parsedPostLoginValues: { [key: string]: string } = postLoginValues ? JSON.parse(postLoginValues) : {};

            if (parsedPostLoginValues?.['partnerSessionUid']) {
                return `/noSuperCookie/${parsedPostLoginValues['partnerSessionUid']}`;
            }

            return '/noSuperCookie/noPartnerSessionId';
        }

        return '';
    }

    private shouldSendPostLogin(): boolean {
        return this.user.isAuthenticated && (this.nativeAppConfig.sendPostLoginOnGoToNative ?? false);
    }

    private navigateToNativeApp(nativeUrl: string, sendPostLogin: boolean, options?: NativeAppGoToOptions) {
        if (options?.replace) {
            this.#window.location.replace(nativeUrl);
        } else if (!sendPostLogin) {
            this.#window.location.href = nativeUrl;
        } else {
            const postLoginValues = this.loginStoreService.PostLoginValues ?? {};
            const additionalParameters = this.cookieService.getObject(CookieName.AdditionalPostLoginOptions) ?? {};
            this.postLoginService.sendPostLoginEvent(postLoginValues, additionalParameters);
        }
    }

    private async handleGoToLastKnownProduct(options?: GoToLastKnownProductOptions, isAsync?: boolean): Promise<void> {
        const defaultOptions = {
            isBackNavigation: true,
            forceHistoryBack: false,
            forceReload: false,
        };
        const opts: GoToLastKnownProductOptions = {
            ...defaultOptions,
            ...options,
        };

        if (opts.forceHistoryBack && this.#window.history.length && this.previousLocation) {
            this.#window.history.back();
        } else {
            const lastKnownProduct = this.lastKnownProductService.get();
            const backlink = this.urlService.parse(lastKnownProduct.url);
            const goToOptions = {
                forceReload: opts.forceReload,
                culture: opts.culture || this.page.lang,
                isBackNavigation: !!opts.isBackNavigation,
            };
            isAsync === true ? await this.goTo(backlink, goToOptions) : this.goTo(backlink, goToOptions);
        }
    }

    private addGaTracking(finalUrl: ParsedUrl) {
        const ssoEnabled = this.page.singleSignOnDomains.some((domain: string) => finalUrl.hostname.endsWith(domain));
        const parentURLDomain = this.urlService.current().host();

        if ((ssoEnabled && finalUrl.hostname != parentURLDomain) || this.nativeAppService.htcmdSchemeEnabled) {
            const parentURL = `${this.urlService.current().host()}/${this.urlService.current().culture}`;
            finalUrl.search.append('parentURL', parentURL);
            const visitorId = this.getGaVisitId();

            if (visitorId != '' && visitorId != null) {
                finalUrl.search.append('gavisitid', visitorId);
            }

            if (this.user.isAuthenticated && this.user.ssoToken) {
                finalUrl.search.append('_sso', this.user.ssoToken);
            }

            const gaVersion = this.checkGaVersion();

            if (gaVersion === 3) {
                this.handleGa3(finalUrl);
            }

            if (gaVersion === 4 && this.isApplicableG4()) {
                this.handleGa4(finalUrl);
            }
        }
    }

    private checkGaVersion(): number {
        let gaVersion: number = 0;

        try {
            if (this.#window['google_tag_data'].gl.decorators.length > 0) {
                gaVersion = 4;
            } else if (this.#window['ga']) {
                gaVersion = 3;
            }
        } catch (error: any) {
            gaVersion = 0;
        }

        return gaVersion;
    }

    private getGa3LinkerParameter(): any {
        try {
            const window = <any>this.#window;

            if (window.location.hostname) {
                const regExDomainsG3 = this.page.crossDomainRegExG4;

                if (window.location.hostname.match(regExDomainsG3)) {
                    return window.ga.getAll()[0].get('linkerParam');
                }
            }
        } catch (error) {
            return null;
        }
    }

    private isApplicableG4(): boolean {
        try {
            let configuredDomain = false;

            if (this.#window.location.hostname) {
                const regExDomains = this.page.crossDomainRegExG4;
                configuredDomain = !!this.#window.location.hostname.match(regExDomains);
            }

            return configuredDomain;
        } catch (error) {
            return false;
        }
    }

    private getValuesFromGa4(param: string): string | null {
        try {
            const linkerParamValue = this.#window['ga'].getAll()[0].get('linkerParam');

            let gaglobj = this.#window['google_tag_data'].gl.decorators[0].callback();
            const clientID = gaglobj._ga;
            gaglobj = JSON.stringify(gaglobj);
            const sessionID = gaglobj.split('_ga')[2].split('.')[3];

            switch (param) {
                case 'id':
                    return clientID;
                case 'session':
                    return sessionID;
                case 'linker':
                    return linkerParamValue.replace('_ga=', '');
                default:
                    return null;
            }
        } catch (error) {
            return null;
        }
    }

    private handleGa3(finalUrl: ParsedUrl) {
        const gaLinkerParameter = this.getGa3LinkerParameter();

        if (gaLinkerParameter != null && gaLinkerParameter != '') {
            finalUrl.search.append('_ga', gaLinkerParameter);
        }
    }

    private handleGa4(finalUrl: ParsedUrl) {
        const gaLinkerParameter = this.getValuesFromGa4('linker');
        const gaRawClientId = this.getValuesFromGa4('id');
        const sessionId = this.getValuesFromGa4('session');
        const clientId = this.formatGAClientID(gaRawClientId);

        if (gaLinkerParameter != null && gaLinkerParameter != '') {
            finalUrl.search.append('_ga', gaLinkerParameter);
        }

        if (clientId != null) {
            finalUrl.search.append('gaclientID', clientId);
        }

        if (sessionId != null) {
            finalUrl.search.append('gasessionID', sessionId);
        }
    }

    private formatGAClientID(rawClientId: string | null): string {
        if (rawClientId) {
            const parts = rawClientId.split('.');

            return parts.length >= 3 ? parts.slice(2).join('.') : rawClientId;
        }

        return '';
    }

    private getGaVisitId(): string {
        if (this.#window['ga']?.getAll && typeof this.#window['ga'].getAll === 'function' && this.#window['ga'].getAll()[0]) {
            const clientIdValue = this.#window['ga'].getAll()[0].get('clientId');

            return this.formatGAClientID(clientIdValue);
        }

        return this.cookieService.get(CookieName.GoogleAnalytics) || '';
    }
}
